'use client';

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { razorpayConfig } from '@/lib/razorpayConfig';

/**
 * Opens Razorpay in place and reports back when the payment is verified, instead of
 * navigating to /membership/checkout and then to a thank-you page. Use this wherever a
 * paywall should be cleared without the user losing what they were doing.
 *
 * The `Window.Razorpay` global is declared in RazorpayPaymentButton.tsx.
 */
export function useRazorpayCheckout() {
  const { user } = useAuth();
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isPaying, setIsPaying] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (window.Razorpay) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- mirrors the existing button's script bootstrap
      setIsScriptLoaded(true);
      return;
    }

    const existingScript = document.querySelector('script[src*="checkout.razorpay.com"]');
    if (existingScript) {
      setIsScriptLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = razorpayConfig.scriptUrl;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onload = () => setIsScriptLoaded(true);
    document.head.appendChild(script);
  }, []);

  /**
   * Creates an order, opens the Razorpay modal and verifies the result.
   * Resolves true only once the server has confirmed the payment.
   */
  const pay = useCallback(
    async ({
      planId,
      amount,
      planName,
      onPaid,
      onError,
      onDismiss,
    }: {
      planId: number;
      amount: number;
      planName?: string;
      onPaid: (result: { orderId: string }) => void | Promise<void>;
      onError?: (error: Error) => void;
      onDismiss?: () => void;
    }) => {
      if (!user) {
        onError?.(new Error('Please log in to continue'));
        return;
      }
      if (!isScriptLoaded || !window.Razorpay) {
        onError?.(new Error('Payment gateway is still loading. Try again in a moment.'));
        return;
      }

      setIsPaying(true);

      try {
        const response = await fetch('/api/payment/create-order', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ planId }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Could not start the payment');
        }

        const { data } = await response.json();

        const rzp = new window.Razorpay({
          key: data.keyId,
          amount: Math.round(Number(amount) * 100),
          currency: data.currency || 'INR',
          name: 'Tools by Ghackk',
          description: `${data.planName || planName || 'Full access'} — one-time`,
          order_id: data.orderId,
          prefill: {
            name: data.userName || user.name || '',
            email: data.userEmail || user.email || '',
            contact: user.phone || '',
          },
          notes: {
            planId: String(planId),
            planName: data.planName || planName || '',
            userId: user.id,
          },
          theme: { color: '#F0A020' },
          handler: async (response: {
            razorpay_order_id: string;
            razorpay_payment_id: string;
            razorpay_signature: string;
          }) => {
            try {
              const verifyResponse = await fetch('/api/payment/verify', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                }),
              });

              if (!verifyResponse.ok) {
                const errorData = await verifyResponse.json().catch(() => ({}));
                throw new Error(errorData.error || 'We could not confirm the payment');
              }

              await verifyResponse.json();
              await onPaid({ orderId: response.razorpay_order_id });
            } catch (error) {
              onError?.(error instanceof Error ? error : new Error('Payment verification failed'));
            } finally {
              setIsPaying(false);
            }
          },
          modal: {
            ondismiss: () => {
              setIsPaying(false);
              onDismiss?.();
            },
          },
        });

        rzp.open();
      } catch (error) {
        setIsPaying(false);
        onError?.(error instanceof Error ? error : new Error('Payment failed'));
      }
    },
    [user, isScriptLoaded]
  );

  return { pay, isPaying, isReady: isScriptLoaded };
}
